/**
 * @file ContactSupportModal.jsx
 * @description Full-page support request form. Sends a structured message to the
 *   platform support email via the contact-support edge function. Usable as an
 *   overlay modal or standalone page.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/ContactSupportModal
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { useIsMobile } from "../hooks/useIsMobile";
import { supabase } from "../lib/supabase";
import {
  LockIcon,
  CreditCardIcon,
  ZapIcon,
  ShieldIcon,
  SettingsIcon,
  TrashIcon,
  MessageIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  AlertTriangleIcon,
} from "./Icons";

/**
 * The copy key for a contact-support reply that did not go through, or null when it did. The reasons are
 * supabase/functions/contact-support/handler.ts's (server rule; this only picks the message).
 * @param {Object|null|undefined} data the function's reply body
 * @returns {string|null}
 */
export function supportReplyErrorKey(data) {
  if (!data || data.ok !== false) return null;
  return data.reason === "rate_limited" ? "contactSupport.errRateLimited" : "contactSupport.errSendFailed";
}

export default function ContactSupportModal({ profile, defaultTopic = "", defaultMessage = "", onClose }) {
  // Phones: a bottom sheet that fits the visible viewport (dvh, so the iOS
  // keyboard and browser bars don't push it off screen), tighter padding, a
  // 16px message font (iOS Safari zooms the page on focus for anything under
  // 16px, which is what blew the window past the screen edge), and no autofocus
  // so the keyboard doesn't open over the form on arrival.
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const TOPICS = [
    {
      value: "account_suspended",
      label: t("contactSupport.topicSuspended"),
      desc:  t("contactSupport.topicSuspendedDesc"),
      Icon:  LockIcon,
    },
    {
      value: "listing_paused",
      label: t("contactSupport.topicListing"),
      desc:  t("contactSupport.topicListingDesc"),
      Icon:  AlertTriangleIcon,
    },
    {
      value: "billing_question",
      label: t("contactSupport.topicBilling"),
      desc:  t("contactSupport.topicBillingDesc"),
      Icon:  CreditCardIcon,
    },
    {
      value: "onboarding",
      label: t("contactSupport.topicGettingStarted"),
      desc:  t("contactSupport.topicGettingStartedDesc"),
      Icon:  ZapIcon,
    },
    {
      value: "verification_issue",
      label: t("contactSupport.topicVerification"),
      desc:  t("contactSupport.topicVerificationDesc"),
      Icon:  ShieldIcon,
    },
    {
      value: "technical_problem",
      label: t("contactSupport.topicTechnical"),
      desc:  t("contactSupport.topicTechnicalDesc"),
      Icon:  SettingsIcon,
    },
    {
      value: "account_deletion",
      label: t("contactSupport.topicDeletion"),
      desc:  t("contactSupport.topicDeletionDesc"),
      Icon:  TrashIcon,
    },
    {
      value: "other",
      label: t("contactSupport.topicOther"),
      desc:  t("contactSupport.topicOtherDesc"),
      Icon:  MessageIcon,
    },
  ];

  const [step,    setStep]    = useState(defaultTopic ? "message" : "topic"); // "topic" | "message" | "sent"
  const [topic,   setTopic]   = useState(defaultTopic || "");
  // defaultMessage seeds the textarea so callers can pre-fill case
  // context (case id, type, location, flag reason) for flagged-case
  // escalations. Editable — agency can amend before sending.
  const [message, setMessage] = useState(defaultMessage || "");
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");

  const name  = profile?.full_name || "";
  const email = profile?.email     || "";

  const selectedTopic = TOPICS.find(tp => tp.value === topic);
  const canSubmit = message.trim().length >= 10 && !sending;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSending(true);
    setError("");
    try {
      // invoke() attaches the user's auth JWT automatically (the function now
      // requires it and derives identity from the token, so we only send the
      // topic + message — identity fields are ignored server-side). Replaces an
      // older no-cors fire-and-forget that couldn't send the Authorization header.
      const { data, error: fnErr } = await supabase.functions.invoke("contact-support", {
        body: { topic, message: message.trim() },
      });
      if (fnErr) throw fnErr;
      // Refusals come back as 200 { ok:false, reason } (a rate limit, a send that failed): not "sent".
      const failKey = supportReplyErrorKey(data);
      if (failKey) { setError(t(failKey)); return; }
      setStep("sent");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const modal = (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 30000,
        background: "rgba(15,23,42,0.6)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
        padding: isMobile ? 0 : 20,
        fontFamily: "'Inter','DM Sans',sans-serif",
      }}
    >
      <div style={{
        background: T.bg,
        borderRadius: isMobile ? "20px 20px 0 0" : 24,
        width: "100%", maxWidth: isMobile ? "100%" : 560,
        boxShadow: "0 40px 100px rgba(0,0,0,0.4)",
        overflow: "hidden",
        display: "flex", flexDirection: "column",
        maxHeight: isMobile ? "94dvh" : "92vh",
        paddingBottom: isMobile ? "env(safe-area-inset-bottom)" : undefined,
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)",
          padding: isMobile ? "20px 20px 16px" : "28px 32px 24px",
          flexShrink: 0,
          position: "relative",
        }}>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            style={{
              position: "absolute", top: 18, right: 18,
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "50%", width: 32, height: 32, cursor: "pointer",
              fontSize: 16, color: "rgba(255,255,255,0.7)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >✕</button>

          {/* Breadcrumb */}
          {step === "message" && (
            <button
              onClick={() => { setStep("topic"); setError(""); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", fontSize: 12, padding: 0, marginBottom: 12, display: "flex", alignItems: "center", gap: 4 }}
            >
              <ChevronLeftIcon size={14} color="rgba(255,255,255,0.5)" /> {t("common.back")}
            </button>
          )}

          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
            {step === "sent" ? t("contactSupport.messageSent") : t("contactSupport.title")}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
            {step === "topic"   && t("contactSupport.whatCanWeHelp")}
            {step === "message" && selectedTopic && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <selectedTopic.Icon size={14} color="rgba(255,255,255,0.5)" />
                {selectedTopic.label}
              </span>
            )}
            {step === "sent"    && t("contactSupport.receivedMessage")}
          </div>

          {/* Sender strip */}
          {(name || email) && step !== "sent" && (
            <div style={{
              marginTop: 16, display: "flex", alignItems: "center", gap: 10,
              background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 12px",
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(255,255,255,0.15)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 13, flexShrink: 0,
              }}>
                {name?.[0]?.toUpperCase() || "?"}
              </div>
              <div>
                {name  && <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{name}</div>}
                {email && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{email}</div>}
              </div>
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY: "auto", flex: 1, padding: isMobile ? "18px 20px" : "28px 32px" }}>

          {/* STEP 1: Topic selection */}
          {step === "topic" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TOPICS.map(tp => (
                <button
                  key={tp.value}
                  onClick={() => { setTopic(tp.value); setStep("message"); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "14px 18px", borderRadius: 14, cursor: "pointer",
                    border: `1.5px solid ${T.border}`,
                    background: T.surface,
                    textAlign: "left", width: "100%",
                    transition: "border-color 0.12s, box-shadow 0.12s",
                    fontFamily: "'Inter','DM Sans',sans-serif",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.boxShadow = `0 0 0 3px ${T.blueLight}`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}><tp.Icon size={20} color={T.textMid} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 2 }}>{tp.label}</div>
                    <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.4 }}>{tp.desc}</div>
                  </div>
                  <span style={{ color: T.textLight, fontSize: 16, flexShrink: 0 }}>›</span>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: Message */}
          {step === "message" && (
            <div>
              {selectedTopic && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 16px", borderRadius: 12, marginBottom: 24,
                  background: T.blueLight, border: `1px solid ${T.blue}33`,
                }}>
                  <span style={{ display: "flex", alignItems: "center" }}><selectedTopic.Icon size={18} color={T.blue} /></span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.blue }}>{selectedTopic.label}</div>
                    <div style={{ fontSize: 12, color: T.textMid }}>{selectedTopic.desc}</div>
                  </div>
                </div>
              )}

              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.textMid, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {t("contactSupport.yourMessage")} <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={isMobile ? 5 : 7}
                autoFocus={!isMobile}
                placeholder={
                  topic === "account_suspended"  ? "Please describe what happened and why you believe your account should be reinstated…" :
                  topic === "listing_paused"     ? "Tell us why you think the pause should be lifted, what you've changed, or any other context that helps us review…" :
                  topic === "billing_question"    ? "Please describe your billing question or provide any relevant invoice/charge details…" :
                  topic === "onboarding"          ? "What are you trying to set up? Where are you getting stuck?" :
                  topic === "verification_issue"  ? "Please describe your verification issue and include your credential number if applicable…" :
                  topic === "technical_problem"   ? "Please describe the issue, what you were doing when it happened, and which device/browser you're using…" :
                  "Please describe your question or issue in as much detail as possible…"
                }
                style={{
                  width: "100%", borderRadius: 12,
                  border: `1.5px solid ${T.border}`, padding: "14px 16px",
                  fontSize: isMobile ? 16 : 13, fontFamily: "'Inter','DM Sans',sans-serif",
                  color: T.text, resize: "vertical",
                  background: T.surface, boxSizing: "border-box",
                  outline: "none", lineHeight: 1.65,
                }}
                onFocus={e => { e.target.style.borderColor = T.blue; }}
                onBlur={e =>  { e.target.style.borderColor = T.border; }}
              />
              <div style={{ fontSize: 11, color: message.trim().length > 0 && message.trim().length < 10 ? T.orange : T.textLight, marginTop: 5 }}>
                {message.trim().length > 0 && message.trim().length < 10
                  ? `${10 - message.trim().length} more characters required`
                  : t("contactSupport.replyNote")}
              </div>

              {error && (
                <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                  ⚠ {error}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Sent */}
          {step === "sent" && (
            <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
              <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
                <CheckCircleIcon size={56} color="#22c55e" />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 10 }}>
                {t("contactSupport.sentConfirm")}
              </div>
              <div style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, maxWidth: 380, margin: "0 auto" }}>
                {email
                  ? t("contactSupport.followUpEmail", { email })
                  : t("contactSupport.followUpDefault")}
              </div>
              {selectedTopic && (
                <div style={{ marginTop: 18, display: "inline-flex", alignItems: "center", gap: 8, background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 16px" }}>
                  <selectedTopic.Icon size={14} color={T.textMid} />
                  <span style={{ fontSize: 13, color: T.textMid }}>{selectedTopic.label}</span>
                </div>
              )}
              <button
                onClick={onClose}
                style={{
                  display: "block", margin: "28px auto 0", padding: "12px 36px",
                  borderRadius: 12, border: "none",
                  background: T.blue, color: "#fff",
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'Inter','DM Sans',sans-serif",
                }}
              >
                {t("common.done")}
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {step === "message" && (
          <div style={{
            flexShrink: 0, padding: isMobile ? "12px 20px 16px" : "16px 32px 28px",
            borderTop: `1px solid ${T.border}`,
            display: "flex", gap: 10, justifyContent: "flex-end",
            background: T.surface,
          }}>
            <button
              onClick={() => { setStep("topic"); setError(""); }}
              style={{
                padding: "11px 22px", borderRadius: 10,
                border: `1px solid ${T.border}`, background: T.surface,
                color: T.textMid, fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "'Inter','DM Sans',sans-serif",
              }}
            >{t("common.back")}</button>
            <button
              onClick={handleSend}
              disabled={!canSubmit}
              style={{
                padding: "11px 28px", borderRadius: 10, border: "none",
                background: canSubmit ? T.blue : T.border,
                color: canSubmit ? "#fff" : T.textLight,
                fontSize: 13, fontWeight: 700,
                cursor: canSubmit ? "pointer" : "not-allowed",
                fontFamily: "'Inter','DM Sans',sans-serif",
              }}
            >
              {sending ? t("common.sending") : t("common.send")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
